/**
 * auth.js — Token + User management + Axios interceptors for Revolead Construction App
 *
 * - Token stored in localStorage (key: revolead_auth_token)
 * - Current user stored in localStorage (key: revolead_current_user)
 * - Axios request interceptor auto-injects Authorization: Bearer <token>
 * - Axios response interceptor catches 401 → clearToken + redirect /login
 * - Login response now includes full user object: {id, username, email, full_name, role, active}
 */

import axios from 'axios';
import { setCurrentCompany } from './brandConfig';

const TOKEN_KEY = 'revolead_auth_token';
const USER_KEY = 'revolead_current_user';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Token helpers ────────────────────────────────────────────────────────────

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('revolead_company');
}

// ── User helpers ────────────────────────────────────────────────────────────

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setCurrentUser(user) {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }
}

export function isAuthenticated() {
  return !!getToken();
}

// ── Role helpers ────────────────────────────────────────────────────────────

const ROLE_RANK = {
  system_admin:  4,
  company_admin: 3,
  project_admin: 2,
  estimator:     1,
  // legacy alias
  admin:         3,
};

function getRoleRank(role) {
  return ROLE_RANK[role] || 0;
}

export function isSystemAdmin() {
  return getCurrentUser()?.role === 'system_admin';
}

/** company_admin OR system_admin (can manage users) */
export function isCompanyAdmin() {
  const role = getCurrentUser()?.role;
  return role === 'system_admin' || role === 'company_admin' || role === 'admin';
}

/** Can manage users (company_admin+) */
export function canManageUsers() {
  return isCompanyAdmin();
}

/** project_admin and above — full platform access */
export function canAccessAllFeatures() {
  return getRoleRank(getCurrentUser()?.role) >= ROLE_RANK.project_admin;
}

/** Estimator only — quote management + view-only for everything else */
export function isEstimatorOnly() {
  return getCurrentUser()?.role === 'estimator';
}

/** Backward compat — isAdmin() = company_admin or above */
export function isAdmin() {
  return isCompanyAdmin();
}

/** Backward compat — any authenticated user can at least view quotes */
export function isEstimator() {
  return getRoleRank(getCurrentUser()?.role) >= ROLE_RANK.estimator;
}

// ── Login / Me API calls ────────────────────────────────────────────────────

export async function login(username, password, companyId = '') {
  const body = { username, password };
  if (companyId) body.company_id = companyId;
  const res = await axios.post(`${API}/auth/login`, body);
  const { access_token, user, company } = res.data;
  setToken(access_token);
  if (user) {
    setCurrentUser(user);
  }
  // Store company branding from login response
  if (company) {
    setCurrentCompany(company);
  }
  return res.data;
}

export async function refreshCurrentUser() {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCurrentUser(res.data);
    return res.data;
  } catch {
    clearToken();
    return null;
  }
}

// ── Axios interceptors ──────────────────────────────────────────────────────

// Request interceptor: attach Bearer token to every request
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: catch 401 → clear token + redirect to /login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearToken();
      // Avoid redirect loop if already on /login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);
